import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../services/api-client';
import { isStandalone } from '../lib/install';

interface PushState {
    supported: boolean;
    /** iOS only delivers push to an installed app, so this gates the offer. */
    needsInstall: boolean;
    permission: NotificationPermission | 'unsupported';
    subscribed: boolean;
    enable: () => Promise<void>;
    disable: () => Promise<void>;
    error: string | null;
}

// Backed by an explicit ArrayBuffer: a Uint8Array over ArrayBufferLike is not
// a BufferSource as far as the DOM types are concerned, and pushManager wants
// one of those.
const urlBase64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
    // VAPID keys are base64url; atob wants standard base64 with padding.
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

    const bytes = new Uint8Array(new ArrayBuffer(raw.length));
    for (let index = 0; index < raw.length; index += 1) {
        bytes[index] = raw.charCodeAt(index);
    }
    return bytes;
};

export const usePush = (): PushState => {
    const supported =
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

    const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
        supported ? Notification.permission : 'unsupported',
    );
    const [subscribed, setSubscribed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!supported) return;
        void navigator.serviceWorker.ready
            .then((registration) => registration.pushManager.getSubscription())
            .then((subscription) => setSubscribed(subscription !== null))
            .catch(() => setSubscribed(false));
    }, [supported]);

    const enable = useCallback(async () => {
        setError(null);
        try {
            const { available, publicKey } = await apiClient.get<{
                available: boolean;
                publicKey: string | null;
            }>('push/key');
            if (!available || !publicKey) {
                setError('Notifications are not set up on the server yet.');
                return;
            }

            const granted = await Notification.requestPermission();
            setPermission(granted);
            if (granted !== 'granted') {
                setError('Notifications are blocked in your browser settings.');
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                // Required by every browser: a push must always be shown.
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });

            await apiClient.post('push/subscribe', subscription.toJSON());
            setSubscribed(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not turn notifications on');
        }
    }, []);

    const disable = useCallback(async () => {
        setError(null);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await apiClient.post('push/unsubscribe', { endpoint: subscription.endpoint });
                await subscription.unsubscribe();
            }
            setSubscribed(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not turn notifications off');
        }
    }, []);

    const isIos =
        /iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    return {
        supported,
        needsInstall: isIos && !isStandalone(),
        permission,
        subscribed,
        enable,
        disable,
        error,
    };
};
